import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaRocket,
  FaChartLine,
  FaUsers,
  FaTasks,
  FaShieldAlt,
  FaClock,
  FaCheckCircle,
  FaArrowRight
} from 'react-icons/fa';
import PublicNav from '../../components/PublicNav/PublicNav';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import './Landing.css';

const Landing = () => {
  const features = [
    {
      icon: <FaTasks />,
      title: 'Task Management',
      description: 'Organize and track tasks efficiently with our intuitive interface'
    },
    {
      icon: <FaUsers />,
      title: 'Team Collaboration',
      description: 'Work together seamlessly with real-time updates and notifications'
    },
    {
      icon: <FaChartLine />,
      title: 'Analytics & Reports',
      description: 'Get insights with powerful analytics and customizable reports'
    },
    {
      icon: <FaClock />,
      title: 'Time Tracking',
      description: 'Track time spent on projects and tasks accurately'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security to keep your data safe'
    },
    {
      icon: <FaRocket />,
      title: 'Fast & Scalable',
      description: 'Built to scale with your growing business needs'
    }
  ];

  const stats = [
    { number: '10K+', label: 'Active Users' },
    { number: '50K+', label: 'Projects Managed' },
    { number: '99.9%', label: 'Uptime' },
    { number: '24/7', label: 'Support' }
  ];

  return (
    <div className="landing-page">
      <PublicNav />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">
              Manage Projects Like a <span className="gradient-text">Pro</span>
            </h1>
            <p className="hero-subtitle">
              The all-in-one project portfolio management platform that helps teams 
              collaborate, track progress, and deliver results faster.
            </p>
            <div className="hero-buttons">
              <Link to="/register" className="btn btn-primary btn-lg">
                Get Started Free <FaArrowRight />
              </Link>
              <Link to="/pricing" className="btn btn-outline btn-lg">
                View Pricing
              </Link>
            </div>
          </motion.div>
          
          <motion.div
            className="hero-image"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="dashboard-preview">
              <div className="preview-header">
                <div className="preview-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="preview-content">
                <div className="preview-sidebar"></div>
                <div className="preview-main">
                  <div className="preview-card"></div>
                  <div className="preview-card"></div>
                  <div className="preview-card"></div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="stat-number">{stat.number}</h3>
                <p className="stat-label">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Powerful Features</h2>
            <p className="section-subtitle">
              Everything you need to manage projects effectively
            </p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-subtitle">
              Join thousands of teams already using our platform
            </p>
            <div className="cta-buttons">
              <Link to="/register" className="btn btn-primary btn-lg">
                Start Free Trial
              </Link>
              <Link to="/contact" className="btn btn-outline-light btn-lg">
                Contact Sales
              </Link>
            </div>
            <div className="cta-features">
              <span><FaCheckCircle /> No credit card required</span>
              <span><FaCheckCircle /> 14-day free trial</span>
              <span><FaCheckCircle /> Cancel anytime</span>
            </div>
          </motion.div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
};

export default Landing;


